/**
 * 3D rendering of a real reinforced concrete column.
 *
 * Concrete: ExtrudeGeometry from the section polygon (rectangular or L).
 * Bars: vertical cylinders at the (x, y) backend positions.
 * Stirrups: closed polyline following the inset polygon at every spacing.
 *
 * Uses @react-three/fiber + drei. All distances in centimeters; a `scale`
 * factor maps cm to scene units so the camera frames any column size cleanly.
 */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid } from '@react-three/drei';
import * as THREE from 'three';
import type { RebarPosition, SectionType, StirrupOutputs } from './types';
import { getInsetPolygon, getSectionPolygon } from './sectionPolygon';

interface Props {
  sectionType: SectionType;
  L1: number;
  L2: number;
  e1?: number;
  e2?: number;
  height?: number;        // cm, clear height of the column
  rebars: RebarPosition[];
  cover?: number;          // cm, clear cover (default 4)
  stirrupDiameter?: number; // cm, default 0.95 (3/8")
  stirrups?: StirrupOutputs;
}

const COLOR_CONCRETE = '#94A3B8';
const COLOR_REBAR_CORNER = '#F59E0B';
const COLOR_REBAR_L1 = '#3B82F6';
const COLOR_REBAR_L2 = '#8B5CF6';
const COLOR_STIRRUP = '#0891B2';

export function Column3DView({
  sectionType,
  L1,
  L2,
  e1 = 15,
  e2 = 15,
  height = 280,
  rebars,
  cover = 4,
  stirrupDiameter = 0.95,
  stirrups,
}: Props) {
  // Geometry computed once per geometry change
  const polygon = useMemo(
    () => getSectionPolygon(sectionType, L1, L2, e1, e2),
    [sectionType, L1, L2, e1, e2],
  );

  // Map cm -> scene units. Pick scale from the bbox of the polygon.
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const widthX = Math.max(...xs) - Math.min(...xs);
  const widthY = Math.max(...ys) - Math.min(...ys);
  const maxDim = Math.max(widthX, widthY, height);
  const s = 3 / maxDim;

  return (
    <Canvas
      camera={{ position: [3, 2.5, 4], fov: 40, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
    >
      <ambientLight intensity={0.55} />
      <directionalLight position={[5, 5, 5]} intensity={0.9} />
      <directionalLight position={[-5, -2, -3]} intensity={0.25} />

      <Grid
        args={[20, 20]}
        cellSize={0.25}
        cellThickness={0.4}
        cellColor="#475569"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#334155"
        fadeDistance={12}
        fadeStrength={1.2}
        position={[0, -height * s * 0.5 - 0.01, 0]}
        infiniteGrid
      />

      <ConcretePrism polygon={polygon} height={height} scale={s} />
      <Stirrups
        polygon={polygon}
        height={height}
        cover={cover}
        stirrupDiameter={stirrupDiameter}
        stirrupsInfo={stirrups}
        scale={s}
        rebars={rebars}
      />
      <LongitudinalBars rebars={rebars} height={height} scale={s} />
      {/* The stirrup density itself communicates the confinement zones (tighter
          at top/bottom, looser in the middle) so we don't add a translucent
          backdrop — that plane was axis-aligned and stuck out past the L's
          silhouette, reading as a "ghost rectangle". */}

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

/* ===== Concrete prism (ExtrudeGeometry from polygon) ===== */

function ConcretePrism({
  polygon,
  height,
  scale,
}: {
  polygon: ReturnType<typeof getSectionPolygon>;
  height: number;
  scale: number;
}) {
  // Build a THREE.Shape in the section's (x, y) plane WITHOUT mirroring the y
  // axis — Three.js Shape uses standard math coordinates (CCW = front-facing
  // normals). Then translate so the extrusion is centered along z and rotate
  // -90° about X so the extrusion axis points up along world +y. The same
  // rotation maps the original section's +y to world's +z, which is consistent
  // with how the rebar cylinders are placed (position = (x, 0, -y)).
  const { geometry, edges } = useMemo(() => {
    const shape = new THREE.Shape();
    polygon.forEach(([x, y], i) => {
      const sx = x * scale;
      const sy = y * scale;
      if (i === 0) shape.moveTo(sx, sy);
      else shape.lineTo(sx, sy);
    });
    shape.closePath();
    const extrude = new THREE.ExtrudeGeometry(shape, {
      depth: height * scale,
      bevelEnabled: false,
      curveSegments: 1,
    });
    // Center along the extrusion axis (z=0) and rotate so the column stands.
    extrude.translate(0, 0, -(height * scale) / 2);
    extrude.rotateX(-Math.PI / 2);
    const eGeom = new THREE.EdgesGeometry(extrude, 1);
    return { geometry: extrude, edges: eGeom };
  }, [polygon, height, scale]);

  return (
    <group>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color={COLOR_CONCRETE}
          transparent
          opacity={0.32}
          roughness={0.85}
          metalness={0.05}
          side={THREE.DoubleSide}
        />
      </mesh>
      <lineSegments geometry={edges}>
        <lineBasicMaterial color="#94A3B8" linewidth={1.5} />
      </lineSegments>
    </group>
  );
}

/* ===== Longitudinal bars (cylinders) ===== */

function LongitudinalBars({
  rebars,
  height,
  scale,
}: {
  rebars: RebarPosition[];
  height: number;
  scale: number;
}) {
  const h = height * scale;
  const colorOf = (kind: RebarPosition['kind']): string => {
    if (kind === 'corner') return COLOR_REBAR_CORNER;
    if (kind === 'intermediate_L1') return COLOR_REBAR_L1;
    return COLOR_REBAR_L2;
  };

  return (
    <group>
      {rebars.map((bar, i) => {
        const radius = Math.sqrt(bar.area / Math.PI) * scale;
        // After the rotateX(-π/2) on the prism: section (x, y) -> world (x, -y).
        // We want the bars co-located with the prism, so we mirror y too.
        return (
          <mesh key={i} position={[bar.x * scale, 0, -bar.y * scale]}>
            <cylinderGeometry args={[radius, radius, h, 16]} />
            <meshStandardMaterial color={colorOf(bar.kind)} metalness={0.4} roughness={0.4} />
          </mesh>
        );
      })}
    </group>
  );
}

/* ===== Stirrups (polyline following inset polygon) ===== */

function Stirrups({
  polygon,
  height,
  cover,
  stirrupDiameter,
  stirrupsInfo,
  scale,
  rebars,
}: {
  polygon: ReturnType<typeof getSectionPolygon>;
  height: number;
  cover: number;
  stirrupDiameter: number;
  stirrupsInfo?: StirrupOutputs;
  scale: number;
  rebars: RebarPosition[];
}) {
  // Drawing convention: the stirrup loop is drawn at the CENTERS of the corner
  // longitudinal bars so it visually "ties" them (matches the backend's
  // corner_offset = cover + d_stirrup + d_corner_bar/2). Fall back to the
  // wire centerline (cover + d_stirrup/2) only if no corner bars are known.
  const cornerAreas = rebars.filter((r) => r.kind === 'corner').map((r) => r.area);
  const cornerBarArea = cornerAreas.length > 0 ? Math.max(...cornerAreas) : 0;
  const cornerBarDiameter = cornerBarArea > 0 ? 2 * Math.sqrt(cornerBarArea / Math.PI) : 0;
  const inset = cornerBarDiameter > 0
    ? cover + stirrupDiameter + cornerBarDiameter / 2
    : cover + stirrupDiameter / 2;
  const inner = useMemo(() => getInsetPolygon(polygon, inset), [polygon, inset]);

  // Build the closed-loop geometry in scene units (xz plane)
  const loopGeom = useMemo(() => {
    const pts: THREE.Vector3[] = inner.map(([x, y]) => new THREE.Vector3(x * scale, 0, -y * scale));
    pts.push(pts[0]!.clone());
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [inner, scale]);

  // Stirrup vertical positions
  const positions = useMemo(() => {
    const sConf = stirrupsInfo?.spacing_confinement ?? 10;
    const sCentral = stirrupsInfo?.spacing_central ?? 25;
    const Lo = stirrupsInfo?.confinement_length ?? Math.max(height / 6, 50);
    const ys: number[] = [];

    let y = stirrupDiameter / 2 + sConf / 2;
    while (y <= Lo) {
      ys.push(-height / 2 + y);
      y += sConf;
    }
    let yc = Lo + sCentral / 2;
    while (yc < height - Lo) {
      ys.push(-height / 2 + yc);
      yc += sCentral;
    }
    let yt = stirrupDiameter / 2 + sConf / 2;
    while (yt <= Lo) {
      ys.push(height / 2 - yt);
      yt += sConf;
    }
    return ys;
  }, [height, stirrupsInfo, stirrupDiameter]);

  return (
    <group>
      {positions.map((y, i) => (
        <line key={i} position={[0, y * scale, 0]}>
          <primitive attach="geometry" object={loopGeom} />
          <lineBasicMaterial color={COLOR_STIRRUP} linewidth={1.5} />
        </line>
      ))}
    </group>
  );
}

